import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Mail, User, CircleAlert as AlertCircle, ArrowRight, UserPlus } from 'lucide-react';

const PARTICLES = Array.from({ length: 12 }, (_, i) => ({
  delay: i * 1.2,
  size: 2 + (i % 3) * 1.5,
  left: `${(i * 8.3 + 4) % 100}%`,
  duration: 10 + (i % 4) * 2,
}));

const FloatingParticle: React.FC<{ delay: number; size: number; left: string; duration: number }> = ({
  delay, size, left, duration
}) => (
  <div
    className="auth-particle"
    style={{
      left,
      width: size,
      height: size,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
    }}
  />
);

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [referralCode] = useState(searchParams.get('ref') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password: Math.random().toString(36).slice(-12),
        options: {
          data: {
            full_name: name,
            referral_code: referralCode || undefined,
          },
        },
      });

      if (signUpError) {
        if (signUpError.message === 'User already registered') {
          throw new Error('This email is already registered. Please try logging in instead.');
        }
        throw signUpError;
      }

      if (!data.user) {
        throw new Error('Signup failed. Please try again.');
      }

      if (referralCode && data.user) {
        try {
          const { data: refData, error: refError } = await supabase.rpc('process_referral_signup', {
            p_referred_user_id: data.user.id,
            p_referral_code: referralCode,
          });

          if (refError) {
            console.error('Referral RPC error:', refError);
          } else if (refData?.error) {
            console.error('Referral processing returned error:', refData.error);
          }
        } catch (refErr) {
          console.error('Referral processing exception:', refErr);
        }
      }

      navigate('/audition-agreement');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=1920&q=80"
          alt="Concert crowd with stage lights"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-purple-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-950/50 via-transparent to-purple-950/30" />
      </div>

      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {PARTICLES.map((p, i) => (
          <FloatingParticle key={i} {...p} />
        ))}

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/TSL_Logo.png"
              alt="The Singing League"
              className="h-24 w-24 object-contain"
            />
            <span className="text-white font-bold text-2xl tracking-tight">The Singing League</span>
          </Link>

          <div className={`max-w-md transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '300ms' }}>
            <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
              Start your
              <span className="block mt-1 landing-text-gradient">audition today.</span>
            </h2>
            <p className="text-white/50 text-lg leading-relaxed">
              Get instant AI-powered feedback on your vocals, compete in tournaments, and climb the leaderboard.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { icon: '1', label: 'Record your audition' },
                { icon: '2', label: 'Get AI scoring' },
                { icon: '3', label: 'Join tournaments' },
              ].map((step) => (
                <div key={step.label} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center mx-auto mb-3">
                    <span className="text-violet-400 font-bold text-sm">{step.icon}</span>
                  </div>
                  <p className="text-white/40 text-xs leading-snug">{step.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className={`flex gap-8 transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '600ms' }}>
            {[
              { label: 'Free to Start', value: '$0' },
              { label: 'Setup Time', value: '< 1min' },
              { label: 'Songs Available', value: '500+' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-white font-bold text-xl">{stat.value}</p>
                <p className="text-white/30 text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 backdrop-blur-xl bg-purple-950/20" />
        <div className="absolute inset-0 auth-grid-pattern opacity-[0.02]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/8 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-500/8 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 w-full max-w-md px-8 sm:px-12">
          <div className={`lg:hidden flex items-center gap-3 mb-12 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/TSL_Logo.png"
                alt="The Singing League"
                className="h-24 w-24 object-contain"
              />
              <span className="text-white font-bold text-2xl tracking-tight">The Singing League</span>
            </Link>
          </div>

          <div className={`mb-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '100ms' }}>
            <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">Join the Spotlight</h1>
            <p className="text-white/40">Create an account to start your audition</p>
          </div>

          <div className={`transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '200ms' }}>
            {error && (
              <div className="bg-red-500/8 border border-red-500/15 rounded-xl p-4 mb-6 flex items-start backdrop-blur-sm auth-shake">
                <AlertCircle className="text-red-400 mr-3 mt-0.5 flex-shrink-0" size={16} />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSignup} className="space-y-5">
              {referralCode && (
                <div className="flex items-center gap-2 bg-teal-500/8 border border-teal-500/15 rounded-xl px-4 py-3 mb-1">
                  <UserPlus size={16} className="text-teal-400 shrink-0" />
                  <p className="text-teal-300 text-sm">You were invited! Referral code: <span className="font-mono font-medium">{referralCode}</span></p>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Name</label>
                <div className={`relative group rounded-xl transition-all duration-300 ${
                  focusedField === 'name' ? 'auth-input-glow' : ''
                }`}>
                  <User className={`absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200 ${
                    focusedField === 'name' ? 'text-violet-400' : 'text-white/25'
                  }`} size={16} />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full pl-11 pr-4 py-3.5 bg-white/[0.05] border border-white/[0.08] text-white rounded-xl focus:outline-none focus:border-violet-400/40 focus:bg-white/[0.07] transition-all duration-300 placeholder:text-white/20 text-sm"
                    placeholder="Your full name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Email</label>
                <div className={`relative group rounded-xl transition-all duration-300 ${
                  focusedField === 'email' ? 'auth-input-glow' : ''
                }`}>
                  <Mail className={`absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200 ${
                    focusedField === 'email' ? 'text-violet-400' : 'text-white/25'
                  }`} size={16} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full pl-11 pr-4 py-3.5 bg-white/[0.05] border border-white/[0.08] text-white rounded-xl focus:outline-none focus:border-violet-400/40 focus:bg-white/[0.07] transition-all duration-300 placeholder:text-white/20 text-sm"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="auth-btn-primary w-full group"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="auth-spinner mr-2" />
                    <span>Setting up your audition...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <span>Start My Audition</span>
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                )}
              </button>
            </form>
          </div>

          <div className={`mt-8 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '400ms' }}>
            <div className="relative flex items-center my-6">
              <div className="flex-1 h-px bg-white/[0.06]" />
              <span className="px-4 text-xs text-white/20 uppercase tracking-wider">Already a member?</span>
              <div className="flex-1 h-px bg-white/[0.06]" />
            </div>

            <Link
              to="/login"
              className="block w-full py-3.5 rounded-xl border border-white/[0.08] text-white/60 text-sm font-medium text-center hover:bg-white/[0.04] hover:border-white/[0.15] hover:text-white/80 transition-all duration-300"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
